import { IAdvertiserGridRow } from '@/Interfaces/Advertiser.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectAdvertisersFilters } from '@/store/selectors/advertisers.selectors';
import { setFilters } from '@/store/slices/advertisers.slice';
import { PaginationState } from '@/types/commonFilters.types';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import COLUMNS from './columns';
import Toolbar from './components/Toolbar';

interface IProps {
  data: IAdvertiserGridRow[];
  rowCount: number;
  loading: boolean;
  handleCreationModalOpen: () => void;
}

const AdvertisersGrid = ({ data = [], rowCount, loading, handleCreationModalOpen }: IProps) => {
  const dispatch = useAppDispatch();

  const { sort: sortModel, ...filters } = useAppSelector(selectAdvertisersFilters);

  const paginationModel = useMemo(() => ({ page: filters.page - 1, pageSize: filters.limit }), [filters]);

  const setPagination = useCallback(
    (pagination: PaginationState) => {
      dispatch(setFilters(pagination));
    },
    [dispatch],
  );
  const setSort = useCallback((sort: GridSortModel | null) => dispatch(setFilters({ sort })), [dispatch]);

  const handlePaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel) => {
      setPagination({ page: newPaginationModel.page + 1, limit: newPaginationModel.pageSize });
    },
    [setPagination],
  );

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      setSort(newSortModel?.length ? newSortModel : null);
    },
    [setSort],
  );

  return (
    <div className='grid grid-cols-1'>
      <Grid
        rows={data}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        showCellVerticalBorder
        showColumnVerticalBorder
        columns={COLUMNS}
        columnHeaderHeight={30}
        sortingMode='server'
        paginationMode='server'
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel ?? []}
        onSortModelChange={handleSortModelChange}
        pagination
        slots={{
          toolbar: Toolbar,
          pagination: ServerSidePagination,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        slotProps={{
          pagination: {
            // @ts-expect-error pagination loading
            loading,
            rowCount,
          },
          footer: {
            className: 'relative',
          },
          toolbar: {
            total: rowCount,
            handleCreationModalOpen,
          },
        }}
      />
    </div>
  );
};

export default AdvertisersGrid;
